// Run inputs: parse a template's inputs, the source chips each kind offers,
// map values for the API, and remember the last values per task.

const LEGACY_KINDS = ['textarea', 'select', 'number', 'date'];

function fromLegacy(list) {
  return list.map((f) => ({
    name: f.name,
    label: f.label || f.name,
    hint: f.description || '',
    required: !!f.required,
    kind: f.type === 'file_upload' ? 'file' : LEGACY_KINDS.includes(f.type) ? f.type : 'text',
    ...(Array.isArray(f.options) ? { options: f.options } : {}),
    schema: 'legacy',
  }));
}

function v2Kind(p) {
  if (p.format === 'data-url') return 'file';
  if (p.format === 'textarea') return 'textarea';
  if (p.format === 'date') return 'date';
  if (Array.isArray(p.enum)) return 'select';
  if (p.type === 'number' || p.type === 'integer') return 'number';
  return 'text';
}

function fromV2(schema) {
  const props = schema.properties || {};
  const keys = Object.keys(props);
  const order = Array.isArray(schema['ui:order']) ? schema['ui:order'].filter((k) => k in props) : [];
  const rest = keys.filter((k) => !order.includes(k));
  const required = schema.required || [];
  return [...order, ...rest].map((key) => {
    const p = props[key] || {};
    const kind = v2Kind(p);
    return {
      name: key,
      label: p.title || key,
      hint: p.description || '',
      required: required.includes(key),
      kind,
      ...(kind === 'select' ? { options: p.enum } : {}),
      schema: 'v2',
    };
  });
}

export function inputsFromTemplate(template) {
  if (Array.isArray(template?.input_schema) && template.input_schema.length) return fromLegacy(template.input_schema);
  const v2 = template?.input_form_schema_v2;
  if (v2 && typeof v2 === 'object' && v2.properties) return fromV2(v2);
  return [];
}

export const SOURCES = [
  { key: 'upload', label: 'Upload' },
  { key: 'mydata', label: 'My data' },
  { key: 'database', label: 'Database query', unfinished: 'inputSourceDatabase' },
  { key: 'storage', label: 'Storage', unfinished: 'inputSourceStorage' },
  { key: 'link', label: 'Link', unfinished: 'inputSourceLink' },
  { key: 'paste', label: 'Paste' },
  { key: 'email', label: 'From an email', unfinished: 'inputSourceEmail' },
  { key: 'output', label: "Another task's output" },
];

const SOURCES_BY_KIND = {
  file: ['upload', 'mydata', 'database', 'storage', 'link', 'email', 'output'],
  text: ['paste', 'link', 'email'],
  textarea: ['paste', 'link', 'email'],
};

export function sourcesFor(kind) {
  return SOURCES_BY_KIND[kind] || [];
}

const isEmpty = (v) => v == null || v === ''
  || (typeof v === 'object' && v.source === 'paste' && !v.text)
  || (typeof v === 'object' && v.source !== 'paste' && !v.file_id);

export function toInputValues(inputs, values) {
  const out = {};
  inputs.forEach((i) => {
    const v = values?.[i.name];
    if (isEmpty(v)) return;
    if (typeof v !== 'object') out[i.name] = v;
    else if (v.source === 'paste') out[i.name] = v.text;
    else {
      const f = { file_id: v.file_id, filename: v.filename };
      out[i.name] = i.schema === 'v2' ? JSON.stringify(f) : f;
    }
  });
  return out;
}

export function missingRequired(inputs, values) {
  return inputs.filter((i) => i.required && isEmpty(values?.[i.name])).map((i) => i.name);
}

// Per user, so a shared browser never shows one person's inputs to another.
const PREFIX = 'flowize.lastInputs.';
const KEY = (userId, templateId) => `${PREFIX}${userId}.${templateId}`;

export function readLastInputs(userId, templateId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(KEY(userId, templateId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.values || typeof parsed.values !== 'object') return null;
    return { values: parsed.values, at: parsed.at };
  } catch (e) {
    return null;
  }
}

export function writeLastInputs(userId, templateId, values, at = new Date().toISOString()) {
  if (!userId) return;
  try {
    localStorage.setItem(KEY(userId, templateId), JSON.stringify({ values, at }));
  } catch (e) { /* quota or private mode: nothing to do */ }
}

/** Forget every user's last inputs (called on sign-out). */
export function clearLastInputs() {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith(PREFIX)).forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* storage unavailable: nothing to clear */ }
}
